import { useId, useState, type SyntheticEvent } from 'react';
import { Link, useSearchParams } from 'react-router';

import {
  AuthField,
  authInputClass,
  AuthLayout,
  AuthNotice,
  AuthSubmit,
} from '../features/auth/AuthLayout';
import { requestPasswordReset } from '../features/auth/session';
import { readTenant } from '../features/auth/tenantMemory';
import { ApiError, NetworkError } from '../lib/api';

function errorMessage(err: unknown): string {
  if (err instanceof NetworkError)
    return 'Não foi possível contatar o servidor. Verifique sua conexão.';
  if (err instanceof ApiError) {
    if (err.code === 'mail_unavailable')
      return 'O envio de e-mails não está configurado nesta instalação. Peça ao administrador da sua organização para redefinir a sua senha.';
    if (err.status === 429) return 'Muitos pedidos. Tente novamente mais tarde.';
    if (err.status === 400) return 'Preencha organização e e-mail.';
  }
  return 'Ocorreu um erro inesperado. Tente novamente.';
}

/** Requests a password reset link (ADR-029). */
export function ForgotPasswordPage() {
  const [params] = useSearchParams();
  const [tenant, setTenant] = useState(() => params.get('tenant') || readTenant());
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const errorId = useId();

  const onSubmit = async (e: SyntheticEvent) => {
    e.preventDefault();
    if (busy) return;
    setError(null);
    setBusy(true);
    try {
      await requestPasswordReset(tenant.trim(), email.trim());
      setSent(true);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout
      title="Esqueci minha senha"
      subtitle="Enviaremos um link para você criar uma nova senha."
    >
      {sent ? (
        <div className="mt-5 space-y-4">
          <AuthNotice tone="success">
            Se existir uma conta com o e-mail <strong>{email.trim()}</strong> na organização{' '}
            <strong>{tenant.trim()}</strong>, enviamos um link para criar uma nova senha. Ele vale
            por 1 hora. Confira também a caixa de spam.
          </AuthNotice>
          <p className="text-center text-[13px]">
            <Link to="/login" className="text-accent hover:underline">
              Voltar ao login
            </Link>
          </p>
        </div>
      ) : (
        <form
          className="mt-5 space-y-4"
          onSubmit={(e) => {
            void onSubmit(e);
          }}
          noValidate
        >
          <AuthField label="Organização" htmlFor="tenant">
            <input
              id="tenant"
              autoComplete="organization"
              autoCapitalize="none"
              spellCheck={false}
              autoFocus={tenant === ''}
              value={tenant}
              onChange={(e) => {
                setTenant(e.target.value);
              }}
              aria-describedby={error ? errorId : undefined}
              className={authInputClass}
              placeholder="ex.: minha-empresa"
            />
          </AuthField>
          <AuthField label="E-mail" htmlFor="email">
            <input
              id="email"
              type="email"
              autoComplete="username"
              autoFocus={tenant !== ''}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
              }}
              aria-describedby={error ? errorId : undefined}
              className={authInputClass}
            />
          </AuthField>
          {error && (
            <AuthNotice id={errorId} tone="error">
              {error}
            </AuthNotice>
          )}
          <AuthSubmit
            busy={busy}
            disabled={!tenant.trim() || !email.trim()}
            label="Enviar link"
            busyLabel="Enviando…"
          />
          <p className="text-center text-[13px]">
            <Link to="/login" className="text-accent hover:underline">
              Voltar ao login
            </Link>
          </p>
        </form>
      )}
    </AuthLayout>
  );
}
